// client/src/pages/PaymentsReview.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import logo from '../assets/chitconnect-logo-blue.png';
import { apiJson, socket } from '../lib/api';
import { fmtLocal, parseAnyDateTime } from '../lib/dates';
import '../App.css';

export default function PaymentsReview({ user }) {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [periods, setPeriods] = useState([]); // [{period, label, ended_at, is_winner}]
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');

  const [loadingReview, setLoadingReview] = useState(false);
  const [loadedPeriod, setLoadedPeriod] = useState('');

  // toast helper (memoized) + safe timer cleanup
  const hideTimerRef = useRef(null);
  const show = useCallback((t, ms = 2500) => {
    setMsg(t);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (ms) hideTimerRef.current = setTimeout(() => setMsg(''), ms);
  }, []);
  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  // Load groups
  useEffect(() => {
    (async () => {
      let r = await apiJson('/api/groups/my');
      if (!r.ok) r = await apiJson('/api/groups'); // fallback
      if (r.ok && Array.isArray(r.data)) setGroups(r.data);
    })();
  }, []);

  const selectedGroup = useMemo(
    () => groups.find(g => String(g.id) === String(groupId)) || null,
    [groups, groupId]
  );

  const selectedPeriod = useMemo(
    () => periods.find(p => String(p.period) === String(period)) || null,
    [periods, period]
  );

  const isSuperadmin =
    String(user?.access_level || '').toLowerCase() === 'superadmin';

  const isOrganiser =
    isSuperadmin ||
    String(selectedGroup?.owner_id || '') === String(user?.id || '') ||
    selectedGroup?.is_owner === true ||
    String(selectedGroup?.role || '').toLowerCase() === 'organiser';

  const isWinnerForPeriod = selectedPeriod?.is_winner === true;

  const canEdit = !!groupId && !!period && (isOrganiser || isWinnerForPeriod);

  // When group changes, load completed periods
  useEffect(() => {
    (async () => {
      setPeriods([]);
      setPeriod('');
      setRows([]);
      setLoadedPeriod('');

      if (!groupId) return;

      const r = await apiJson(`/api/payments/${groupId}/completed-periods`);
      if (!r.ok) {
        show(r.text || 'Failed to load periods');
        return;
      }

      const list = Array.isArray(r.data?.rows) ? r.data.rows : [];
      setPeriods(list);

      if (list.length) setPeriod(String(list[0].period));
    })();
  }, [groupId, show]);

  // Request sequencing to ignore out-of-order responses
  const reqSeqRef = useRef(0);

  const normalizeMembershipIndex = (x) => {
    const mi =
      x?.membership_index ??
      x?.membershipIndex ??
      x?.membership ??
      1;
    const n = Number(mi);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const loadReview = useCallback(async () => {
    if (!groupId || !period) return;

    const seq = ++reqSeqRef.current;
    setLoadingReview(true);

    try {
      const r = await apiJson(`/api/payments/${groupId}/${period}/review`);
      if (seq !== reqSeqRef.current) return;

      if (!r.ok) {
        show(r.text || 'Not allowed or no winner yet');
        setRows([]);
        setLoadedPeriod('');
        return;
      }

      const mapped = (r.data?.rows || []).map((x) => {
        const membership_index = normalizeMembershipIndex(x);

        // ✅ Default is ALWAYS "On time" unless the reviewer explicitly chooses otherwise.
        // NOTE: payments.points defaults to 0 for new rows, so points alone cannot mean "confirmed late".
        // We only treat points >= 10 as an explicit "on time" confirmation.
        const confirmed = (x.on_time_confirmed != null)
          ? !!x.on_time_confirmed
          : (x.points != null && Number(x.points) >= 10 ? true : null);

        const paid = parseAnyDateTime(x.paid_date, { assumeZone: 'utc' });
        const due  = parseAnyDateTime(x.due_date,  { assumeZone: 'utc' });
        const isLate = (paid && due) ? (paid.toMillis() > due.toMillis()) : false;

        const lateLabel = (() => {
          if (!isLate || !paid || !due) return '';
          const mins = Math.ceil((paid.toMillis() - due.toMillis()) / 60000);
          if (mins >= 1440) return `Late by ${Math.ceil(mins / 1440)} day(s)`;
          if (mins >= 60) return `Late by ${Math.ceil(mins / 60)} hour(s)`;
          return `Late by ${mins} min`;
        })();

        const chosen = confirmed == null ? true : !!confirmed;

        return {
          ...x,
          membership_index,
          on_time: chosen,
          is_late: isLate,
          late_label: lateLabel,
          _key: `${x.user_id}_${membership_index}`,
        };
      });

      setRows(mapped);
      setLoadedPeriod(String(period));
    } finally {
      if (seq === reqSeqRef.current) setLoadingReview(false);
    }
  }, [groupId, period, show]);

  useEffect(() => {
    if (!groupId || !period) return;
    setRows([]);
    setLoadedPeriod('');
    loadReview();
  }, [groupId, period, loadReview]);

  // Realtime listeners
  useEffect(() => {
    if (!groupId || !period) return;

    const gid = Number(groupId);
    socket.emit('joinRoom', { groupId: gid });

    const onPaymentsChanged = (p) => {
      if (Number(p?.groupId) === gid && Number(p?.period) === Number(period)) {
        loadReview();
      }
    };
    const onPayoutRequired = (p) => {
      if (Number(p?.groupId) === gid && Number(p?.period) === Number(period)) {
        loadReview();
      }
    };

    socket.on('paymentStatusChanged', onPaymentsChanged);
    socket.on('payoutRequired', onPayoutRequired);

    return () => {
      socket.off('paymentStatusChanged', onPaymentsChanged);
      socket.off('payoutRequired', onPayoutRequired);
      socket.emit('leaveRoom', { groupId: gid });
    };
  }, [groupId, period, loadReview]);

  const setOnTime = (userId, membership_index, valueBool) => {
    setRows(prev => prev.map(x =>
      String(x.user_id) === String(userId) && Number(x.membership_index) === Number(membership_index)
        ? { ...x, on_time: !!valueBool }
        : x
    ));
  };

  const save = async () => {
    if (!groupId || !period) return;

    if (!canEdit) {
      return show('Only the winner (money receiver) or the organiser can save this period.');
    }

    if (String(loadedPeriod) !== String(period)) {
      return show('Period changed. Please click Load, then Save.');
    }

    const body = {
      confirms: rows.map(r => ({
        user_id: r.user_id,
        membership_index: Number(r.membership_index) || 1,
        on_time: !!r.on_time,
      })),
    };

    const r = await apiJson(`/api/payments/${groupId}/${period}/confirm`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!r.ok) return show(r.text || 'Save failed');

    const updated = r.data?.updated;

    if (updated === 0) {
      show('Saved, but 0 rows updated — usually membership_index mismatch or backend not updating.');
    } else {
      show(updated != null ? `Saved (${updated} updated)` : 'Saved');
    }

    loadReview();
  };

  // Helps show Membership # only when needed
  const multiMembership = useMemo(() => {
    const byUser = new Map();
    for (const r of rows) {
      const k = String(r.user_id);
      byUser.set(k, (byUser.get(k) || 0) + 1);
    }
    for (const [, cnt] of byUser) if (cnt > 1) return true;
    return false;
  }, [rows]);

  return (
    <div className="main-content">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <div className="card">
        <div
          className="card-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h2 style={{ margin: 0 }}>Confirm On-Time Payments</h2>
          {loadingReview && <span style={{ fontSize: 13, opacity: 0.75 }}>Loading…</span>}
        </div>

        {msg && <div className="toast-message">{msg}</div>}

        <div style={{ marginBottom: 10, opacity: 0.85, fontSize: 14 }}>
          Only the <b>winner (money receiver)</b> or the <b>group organiser</b> can review a completed period.
          {groupId && period && (
            <span>
              {' '}
              {isOrganiser ? (
                <span><b>Organiser override enabled:</b> your selections will override the winner’s.</span>
              ) : isWinnerForPeriod ? (
                <span>You are the <b>winner</b> for this period. The organiser can still override later.</span>
              ) : (
                <span>Saving is restricted to winner/organiser.</span>
              )}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <select
            className="styled-dropdown"
            value={groupId}
            onChange={e => setGroupId(e.target.value)}
            style={{ maxWidth: 320 }}
          >
            <option value="">— choose group —</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>

          <select
            className="styled-dropdown"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            disabled={!periods.length}
            style={{ width: 180 }}
          >
            {!periods.length && <option value="">— no completed periods —</option>}
            {periods.map(p => (
              <option key={p.period} value={p.period}>{p.label}</option>
            ))}
          </select>

          <button className="btn btn-secondary" onClick={loadReview} disabled={!groupId || !period || loadingReview}>
            Load
          </button>

          <button
            className="btn btn-primary"
            onClick={save}
            disabled={
              !rows.length ||
              loadingReview ||
              !canEdit ||
              String(loadedPeriod) !== String(period)
            }
          >
            Save
          </button>
        </div>

        <table className="styled-table review-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Due</th>
              <th>Paid</th>
              <th>Payment</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r._key}>
                <td data-label="Member">
                  {r.name}
                  {multiMembership && (
                    <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.75 }}>
                      (M#{r.membership_index})
                    </span>
                  )}
                </td>

                <td data-label="Due">{fmtLocal(r.due_date, 'ccc d LLL yyyy, t')}</td>

                <td data-label="Paid">
                  {r.paid_date ? (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                      <span>{fmtLocal(r.paid_date, 'ccc d LLL yyyy, t')}</span>
                      {r.is_late && r.late_label ? (
                        <span style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>{r.late_label}</span>
                      ) : null}
                    </div>
                  ) : (
                    <span style={{ opacity: .7 }}>—</span>
                  )}
                </td>

                <td data-label="Payment">
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label className="radio">
                      <input
                        type="radio"
                        name={`ontime_${r.user_id}_${r.membership_index}`}
                        checked={r.on_time === true}
                        onChange={() => setOnTime(r.user_id, r.membership_index, true)}
                        disabled={!canEdit}
                      />
                      <span style={{ marginLeft: 6 }}>On time (+10)</span>
                    </label>

                    <label className="radio">
                      <input
                        type="radio"
                        name={`ontime_${r.user_id}_${r.membership_index}`}
                        checked={r.on_time === false}
                        onChange={() => setOnTime(r.user_id, r.membership_index, false)}
                        disabled={!canEdit}
                      />
                      <span style={{ marginLeft: 6 }}>Late (0 points)</span>
                    </label>
                  </div>
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', opacity: .6 }}>
                  Choose a group & completed period, then Load.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
